import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  defaultAnimateLayoutChanges,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bars3Icon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GroupsManagement from '../components/GroupsManagement';
import { Organization } from '../components/OrganizationSelector';
import PlanLimitDialog from '../components/PlanLimitDialog';
import {
  getFocusModeTailwindClasses,
  getSelectableFocusModes,
} from '../constants/focusModes';
import { useOrganization } from '../context/OrganizationContext';
import { groupsService } from '../services/groups';
import { Segment as BaseSegment } from '../services/segments';
import { OrganizationGroup } from '../types/groups';
import { getAuthHeaders } from '../utils/auth';
import { formatCurrency } from '../utils/currency';
import {
  formatFocusMode,
  formatMarketMaturity,
  formatSegmentPriority,
} from '../utils/enumFormatting';
import { API_URL } from '../constants/urls';

// Extended Segment type to include additional fields not in the service interface
interface Segment extends BaseSegment {
  obvious_choice_score?: number | null;
  company_count?: number;
  booked_business?: number;
  lost_business?: number;
  open_pipeline_unweighted?: number;
  open_pipeline_weighted?: number;
  owner_name?: string;
  total_addressable_market?: number;
  market_maturity?: string;
  prospect_count?: number;
  customer_count?: number;
  testimonial_count?: number;
  case_study_count?: number;
  award_count?: number;
  group?: { id: string; name: string; color: string | null } | null;
  priority?: string;
  strategic_owner_id?: string;
}

type SegmentFormData = {
  name: string;
  description: string;
  focus_mode: string;
  priority: string;
  strategic_owner_id: string;
  total_addressable_market: number | string;
  market_maturity: string;
  group_id: string | null;
};

const INITIAL_FORM_DATA: SegmentFormData = {
  name: '',
  description: '',
  focus_mode: 'Focus',
  priority: 'Medium',
  strategic_owner_id: '',
  total_addressable_market: '',
  market_maturity: 'emerging',
  group_id: null,
};

// Sortable segment row component
interface SortableSegmentRowProps {
  segment: Segment;
  canEdit: boolean;
  canDelete: boolean;
  reorderMode: boolean;
  onView: (segment: Segment) => void;
  onEdit: (segment: Segment) => void;
  onDelete: (id: string) => void;
  organization: Organization | null;
}

function SortableSegmentRow({
  segment,
  canEdit,
  canDelete,
  reorderMode,
  onView,
  onEdit,
  onDelete,
  organization,
}: SortableSegmentRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    rect,
    node,
    active,
    over,
    isOver,
    index,
  } = useSortable({
    id: segment.id,
    animateLayoutChanges: defaultAnimateLayoutChanges,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getMaturityColor = (maturity: string) => {
    switch (maturity?.toLowerCase()) {
      case 'emerging':
        return 'bg-purple-100 text-purple-800';
      case 'growing':
        return 'bg-blue-100 text-blue-800';
      case 'mature':
        return 'bg-green-100 text-green-800';
      case 'declining':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      {reorderMode && canEdit && (
        <td className="px-2 py-4">
          <button
            {...attributes}
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600"
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
        </td>
      )}
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="flex items-center gap-2">
            {segment.group?.color && (
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: segment.group.color }}
              />
            )}
            <button
              onClick={() => onView(segment)}
              className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left"
            >
              {segment.name}
            </button>
          </div>
          {segment.owner_name && (
            <p className="text-xs text-gray-500 ml-5">{segment.owner_name}</p>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 py-1 rounded-full text-xs ${getFocusModeTailwindClasses(segment.focus_mode, true)}`}
        >
          {formatFocusMode(segment.focus_mode)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(segment.priority || 'medium')}`}
        >
          {formatSegmentPriority(segment.priority || 'medium')}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {segment.total_addressable_market
          ? formatCurrency(
              segment.total_addressable_market,
              organization?.currency || 'USD',
              { compact: true },
            )
          : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 py-1 rounded-full text-xs ${getMaturityColor(segment.market_maturity || '')}`}
        >
          {formatMarketMaturity(segment.market_maturity)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {segment.obvious_choice_score !== null &&
        segment.obvious_choice_score !== undefined
          ? `${segment.obvious_choice_score.toFixed(1)}/10`
          : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <div className="space-y-1">
          <div>
            <span className="text-green-600 font-medium">
              {formatCurrency(
                segment.booked_business || 0,
                organization?.currency || 'USD',
                { compact: true },
              )}
            </span>
            <span className="text-xs text-gray-500 ml-1">Won</span>
          </div>
          <div>
            <span className="text-blue-600 text-sm">
              {formatCurrency(
                segment.open_pipeline_weighted || 0,
                organization?.currency || 'USD',
                { compact: true },
              )}
            </span>
            <span className="text-xs text-gray-500 ml-1">Weighted</span>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <div className="flex gap-3">
          <div className="text-center">
            <div className="font-semibold">{segment.prospect_count || 0}</div>
            <div className="text-xs text-gray-500">Prospects</div>
          </div>
          <div className="text-center">
            <div className="font-semibold">{segment.customer_count || 0}</div>
            <div className="text-xs text-gray-500">Customers</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onView(segment)}
            className="text-blue-600 hover:text-blue-900"
            title="View"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {canEdit && (
            <button
              onClick={() => onEdit(segment)}
              className="text-gray-600 hover:text-gray-900"
              title="Edit"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(segment.id)}
              className="text-red-600 hover:text-red-900"
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function SegmentsTailwind() {
  const navigate = useNavigate();
  const {
    selectedOrg: organization,
    canEdit,
    canDelete,
    userRole,
  } = useOrganization();
  const [segments, setSegments] = useState<Segment[]>([]);
  const [open, setOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [formData, setFormData] = useState<SegmentFormData>(INITIAL_FORM_DATA);
  const [members, setMembers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'segments' | 'groups'>('segments');
  const [groups, setGroups] = useState<OrganizationGroup[]>([]);
  const [organizationDetails, setOrganizationDetails] = useState<any>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [originalSegments, setOriginalSegments] = useState<Segment[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Plan limit dialog state
  const [planLimitDialog, setPlanLimitDialog] = useState<{
    open: boolean;
    limitType: 'user' | 'editor' | 'proposition' | 'segment';
    currentCount?: number;
    limit?: number;
  }>({ open: false, limitType: 'segment' });

  // canEdit and canDelete are already from useOrganization hook

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require drag of 8px to start
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (!organization) return;
    fetchSegments();
    fetchMembers();
    fetchGroups();
    // Fetch organization details to get plan_type
    axios
      .get(`${API_URL}/organizations/${organization.id}`)
      .then((res) => {
        setOrganizationDetails(res.data);
      })
      .catch((_error) => {
        // console.error removed
      });
  }, [organization]);

  const fetchSegments = async () => {
    if (!organization) return;
    try {
      const response = await axios.get(
        `${API_URL}/segments?organization_id=${organization.id}`,
      );
      setSegments(response.data);
    } catch (_error) {
      // console.error removed
    }
  };

  const fetchMembers = async () => {
    if (!organization) return;
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organization.id}/members?organization_id=${organization.id}`,
      );
      setMembers(response.data);
    } catch (_error) {
      // console.error removed
    }
  };

  const fetchGroups = async () => {
    if (!organization) return;
    try {
      const groupsData = await groupsService.getGroups(
        organization.id,
        'segment',
      );
      setGroups(groupsData);
    } catch (_error) {
      // console.error removed
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditingSegment(null);
    setFormData(INITIAL_FORM_DATA);
  };

  const handleEdit = (segment: Segment) => {
    setEditingSegment(segment);
    setFormData({
      name: segment.name || '',
      description: segment.description || '',
      focus_mode: segment.focus_mode || 'focus',
      priority: segment.priority || 'medium',
      strategic_owner_id: segment.strategic_owner_id || '',
      total_addressable_market: segment.total_addressable_market || '',
      market_maturity: segment.market_maturity || 'emerging',
      group_id: segment.group_id || null,
    });
    setOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!organization) return;

    const submitData = {
      ...formData,
      // Always include group_id; send null for No group
      group_id: formData.group_id ? formData.group_id : null,
      total_addressable_market:
        formData.total_addressable_market === ''
          ? null
          : Number(formData.total_addressable_market),
    };

    try {
      if (editingSegment) {
        await axios.put(
          `${API_URL}/segments/${editingSegment.id}?organization_id=${organization.id}`,
          submitData,
        );
      } else {
        await axios.post(
          `${API_URL}/segments?organization_id=${organization.id}`,
          submitData,
        );
      }
      fetchSegments();
      handleClose();
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail;

      // Check if it's a plan limit error
      if (
        typeof errorDetail === 'object' &&
        errorDetail?.error === 'object_limit_exceeded'
      ) {
        setPlanLimitDialog({
          open: true,
          limitType: 'segment',
          currentCount: errorDetail.current_count,
          limit: errorDetail.limit,
        });
        handleClose(); // Close the creation dialog
      } else {
        // console.error removed
        // You could show a regular error message here
      }
    }
  };

  const handleDelete = async (segmentId: string) => {
    if (
      !organization ||
      !window.confirm('Are you sure you want to delete this segment?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/segments/${segmentId}?organization_id=${organization.id}`,
      );
      fetchSegments();
    } catch (error) {
      // console.error removed
    }
  };

  // Stable ordering across modes: first by display_order (if available), then by name
  const orderedSegments = useMemo(() => {
    const clones = [...segments];
    clones.sort((a, b) => {
      const aOrder =
        typeof a.display_order === 'number'
          ? a.display_order
          : Number.MAX_SAFE_INTEGER;
      const bOrder =
        typeof b.display_order === 'number'
          ? b.display_order
          : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });
    return clones;
  }, [segments]);

  // Use raw list during reorder to preserve live drag order; otherwise, use stable ordered list
  const listForRender = reorderMode ? segments : orderedSegments;

  const filteredSegments = listForRender.filter((segment) =>
    segment.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Group by segment groups only; fall back to 'Ungrouped'
  const segGroupNamesById = useMemo(() => {
    const m = new Map<string, string>();
    groups
      .filter((g) => g.group_type === 'segment')
      .forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const groupedSegments = filteredSegments.reduce(
    (acc, seg) => {
      const group =
        (seg.group_id && segGroupNamesById.get(seg.group_id)) || 'Ungrouped';
      if (!acc[group]) acc[group] = [];
      acc[group].push(seg);
      return acc;
    },
    {} as Record<string, Segment[]>,
  );

  // Ensure groups are ordered by display_order from fetched groups, Ungrouped last
  const sortedGroupEntries = useMemo(() => {
    const orderIndex = new Map<string, number>();
    groups
      .filter((g) => g.group_type === 'segment')
      .forEach((g, i) =>
        orderIndex.set(
          g.name,
          g.name === 'Ungrouped'
            ? Number.MAX_SAFE_INTEGER
            : (g.display_order ?? i),
        ),
      );
    const entries = Object.entries(groupedSegments);
    entries.sort(([a], [b]) => {
      const aOrder = orderIndex.has(a)
        ? (orderIndex.get(a) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      const bOrder = orderIndex.has(b)
        ? (orderIndex.get(b) as number)
        : Number.MAX_SAFE_INTEGER - 1;
      return aOrder - bOrder;
    });
    return entries;
  }, [groupedSegments, groups]);

  // Handle drag end for reordering - only updates local state
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Normalize group names using segment groups only
    const segGroupsMap = new Map(
      groups
        .filter((g) => g.group_type === 'segment')
        .map((g) => [g.id, g.name]),
    );
    const normalizeGroupName = (s?: Segment) =>
      s && s.group_id && segGroupsMap.has(s.group_id)
        ? (segGroupsMap.get(s.group_id) as string)
        : 'Ungrouped';

    const activeItem = segments.find((s) => s.id === active.id);
    const overItem = segments.find((s) => s.id === over.id);
    if (!activeItem || !overItem) return;

    const activeGroupName = normalizeGroupName(activeItem);
    const overGroupName = normalizeGroupName(overItem);

    // Restrict to same group in reorder mode
    if (reorderMode && activeGroupName !== overGroupName) {
      return;
    }

    const groupName = activeGroupName;
    const groupItems = filteredSegments.filter(
      (s) => normalizeGroupName(s) === groupName,
    );
    const idsInGroup = groupItems.map((s) => s.id);
    const oldIndex = idsInGroup.indexOf(active.id as string);
    const newIndex = idsInGroup.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedGroup = arrayMove(groupItems, oldIndex, newIndex);

      // Build new full list with the group replaced by reorderedGroup
      const newList: Segment[] = [];
      let gi = 0;
      for (const s of segments) {
        if (normalizeGroupName(s) === groupName) {
          newList.push(reorderedGroup[gi++] ?? s);
        } else {
          newList.push(s);
        }
      }

      // Reindex display_order within the affected group for stability
      const reindexed = newList.map((s) => ({
        ...s,
        display_order:
          normalizeGroupName(s) === groupName
            ? reorderedGroup.findIndex((x) => x.id === s.id)
            : typeof s.display_order === 'number'
              ? s.display_order
              : Number.MAX_SAFE_INTEGER,
      }));

      setSegments(reindexed);
      setHasChanges(true);
    }
  };

  // Enter reorder mode and save original state
  const handleEnterReorderMode = () => {
    // Clear search when entering reorder mode
    setSearchTerm('');
    // Assign proper display_order values based on current order
    const segmentsWithOrder = segments.map((seg, index) => ({
      ...seg,
      display_order: index,
    }));
    setSegments(segmentsWithOrder);
    setOriginalSegments([...segmentsWithOrder]);
    setReorderMode(true);
    setHasChanges(false);
  };

  // Save the new order to the backend
  const handleSaveOrder = async () => {
    if (!organization || !hasChanges) return;

    setIsSaving(true);

    // Prepare update for API
    const updates = segments.map((seg, index) => ({
      id: seg.id,
      display_order: index,
      group_name: seg.group_name || null,
      group_id: seg.group_id || null,
    }));

    try {
      const response = await axios.put(
        `${API_URL}/segments/update-order?organization_id=${organization.id}`,
        { items: updates },
        {
          headers: getAuthHeaders(),
        },
      );

      // Update with server response
      setSegments(response.data);
      setReorderMode(false);
      setHasChanges(false);
      setOriginalSegments([]);
    } catch (error) {
      console.error('Failed to save segment order:', error);
      alert('Failed to save the new order. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel reordering and restore original state
  const handleCancelReorder = () => {
    setSegments(originalSegments);
    setReorderMode(false);
    setHasChanges(false);
    setOriginalSegments([]);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getMaturityColor = (maturity: string) => {
    switch (maturity?.toLowerCase()) {
      case 'emerging':
        return 'bg-blue-100 text-blue-700';
      case 'growth':
        return 'bg-green-100 text-green-700';
      case 'mature':
        return 'bg-yellow-100 text-yellow-700';
      case 'declining':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (!organization) {
    return (
      <div className="p-6">
        <p className="text-gray-600">
          Please select an organization to view segments.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Market Segments</h1>
        {canEdit && activeTab === 'segments' && (
          <div className="flex gap-2">
            {reorderMode ? (
              <>
                <button
                  onClick={handleCancelReorder}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOrder}
                  className={`px-4 py-2 rounded-md ${
                    hasChanges && !isSaving
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Order'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEnterReorderMode}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  <Bars3Icon className="h-5 w-5" />
                  <span>Reorder</span>
                </button>
                <button
                  onClick={handleOpen}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Add Segment</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Unsaved changes indicator */}
      {reorderMode && hasChanges && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-center">
          <svg
            className="w-5 h-5 text-yellow-600 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm text-yellow-800">
            You have unsaved changes. Click &quot;Save Order&quot; to save or
            &quot;Cancel&quot; to discard.
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('segments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'segments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Segments
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'groups'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Groups
          </button>
        </nav>
      </div>

      {activeTab === 'segments' ? (
        <>
          {/* Search bar */}
          {!reorderMode && (
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search segments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Segments Table/Cards */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {reorderMode && canEdit && (
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Segment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Focus
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      TAM
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maturity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pipeline
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Metrics
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reorderMode
                    ? // Grouped reordering: one SortableContext per group, drag within group only
                      sortedGroupEntries.map(([groupName, segs]) => (
                        <React.Fragment key={`reorder-${groupName}`}>
                          {(Object.keys(groupedSegments).length > 1 ||
                            groupName !== 'Ungrouped') && (
                            <tr className="bg-gray-50">
                              <td
                                colSpan={10}
                                className="px-6 py-2 text-sm font-semibold text-gray-700"
                              >
                                {groupName}
                              </td>
                            </tr>
                          )}
                          <SortableContext
                            items={segs.map((s) => s.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {segs.map((segment) => (
                              <SortableSegmentRow
                                key={segment.id}
                                segment={segment}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                reorderMode={reorderMode}
                                onView={(seg) =>
                                  navigate(`/segments/${seg.id}`)
                                }
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                organization={organization}
                              />
                            ))}
                          </SortableContext>
                        </React.Fragment>
                      ))
                    : sortedGroupEntries.map(([groupName, segs]) => (
                        <React.Fragment key={groupName}>
                          {(Object.keys(groupedSegments).length > 1 ||
                            groupName !== 'Ungrouped') && (
                            <tr className="bg-gray-50">
                              <td
                                colSpan={9}
                                className="px-6 py-2 text-sm font-semibold text-gray-700"
                              >
                                {groupName}
                              </td>
                            </tr>
                          )}
                          {segs.map((segment) => (
                            <tr key={segment.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="flex items-center gap-2">
                                    {segment.group?.color && (
                                      <div
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{
                                          backgroundColor: segment.group.color,
                                        }}
                                      />
                                    )}
                                    <button
                                      onClick={() =>
                                        navigate(`/segments/${segment.id}`)
                                      }
                                      className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left"
                                    >
                                      {segment.name}
                                    </button>
                                  </div>
                                  {segment.owner_name && (
                                    <p className="text-xs text-gray-500 ml-5">
                                      {segment.owner_name}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${getFocusModeTailwindClasses(segment.focus_mode, true)}`}
                                >
                                  {formatFocusMode(segment.focus_mode)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(segment.priority || 'medium')}`}
                                >
                                  {formatSegmentPriority(
                                    segment.priority || 'medium',
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {segment.total_addressable_market
                                  ? formatCurrency(
                                      segment.total_addressable_market,
                                      organization.currency || 'USD',
                                      {
                                        compact: true,
                                      },
                                    )
                                  : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${getMaturityColor(segment.market_maturity || '')}`}
                                >
                                  {formatMarketMaturity(
                                    segment.market_maturity,
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {segment.obvious_choice_score !== null &&
                                segment.obvious_choice_score !== undefined
                                  ? `${segment.obvious_choice_score.toFixed(1)}/10`
                                  : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="space-y-1">
                                  <div>
                                    <span className="text-green-600 font-medium">
                                      {formatCurrency(
                                        segment.booked_business || 0,
                                        organization.currency || 'USD',
                                        {
                                          compact: true,
                                        },
                                      )}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-1">
                                      Won
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-blue-600 text-sm">
                                      {formatCurrency(
                                        segment.open_pipeline_weighted || 0,
                                        organization.currency || 'USD',
                                        {
                                          compact: true,
                                        },
                                      )}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-1">
                                      Weighted
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex gap-3">
                                  <div className="text-center">
                                    <div className="font-semibold">
                                      {segment.prospect_count || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Prospects
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-semibold">
                                      {segment.customer_count || 0}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      Customers
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() =>
                                      navigate(`/segments/${segment.id}`)
                                    }
                                    className="text-blue-600 hover:text-blue-900"
                                    title="View"
                                  >
                                    <EyeIcon className="h-5 w-5" />
                                  </button>
                                  {canEdit && (
                                    <button
                                      onClick={() => handleEdit(segment)}
                                      className="text-yellow-600 hover:text-yellow-900"
                                      title="Edit"
                                    >
                                      <PencilIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      onClick={() => handleDelete(segment.id)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete"
                                    >
                                      <TrashIcon className="h-5 w-5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                </tbody>
              </table>
            </div>
          </DndContext>

          {/* Mobile view - Cards */}
          <div className="lg:hidden space-y-4">
            {sortedGroupEntries.map(([groupName, segs]) => (
              <div key={groupName}>
                {(Object.keys(groupedSegments).length > 1 ||
                  groupName !== 'Ungrouped') && (
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    {groupName}
                  </h3>
                )}
                {segs.map((segment) => (
                  <div
                    key={segment.id}
                    className="bg-white rounded-lg shadow p-4 mb-4"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          {segment.group?.color && (
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: segment.group.color }}
                            />
                          )}
                          <button
                            onClick={() => navigate(`/segments/${segment.id}`)}
                            className="font-semibold text-lg text-gray-900 hover:text-blue-600 text-left"
                          >
                            {segment.name}
                          </button>
                        </div>
                        {segment.owner_name && (
                          <p className="text-xs text-gray-500 ml-5">
                            {segment.owner_name}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${getFocusModeTailwindClasses(segment.focus_mode, true)}`}
                        >
                          {formatFocusMode(segment.focus_mode)}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(segment.priority || 'medium')}`}
                        >
                          {formatSegmentPriority(segment.priority || 'medium')}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">TAM</p>
                        <p className="font-medium">
                          {segment.total_addressable_market
                            ? formatCurrency(
                                segment.total_addressable_market,
                                organization.currency || 'USD',
                                {
                                  compact: true,
                                },
                              )
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Maturity</p>
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs ${getMaturityColor(segment.market_maturity || '')}`}
                        >
                          {formatMarketMaturity(segment.market_maturity)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Score</p>
                        <p className="font-medium">
                          {segment.obvious_choice_score !== null &&
                          segment.obvious_choice_score !== undefined
                            ? `${segment.obvious_choice_score.toFixed(1)}/10`
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Won Business</p>
                        <p className="font-medium text-green-600">
                          {formatCurrency(
                            segment.booked_business || 0,
                            organization.currency || 'USD',
                            {
                              compact: true,
                            },
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t">
                      <div className="flex gap-4 text-sm">
                        <div>
                          <span className="font-semibold">
                            {segment.prospect_count || 0}
                          </span>
                          <span className="text-gray-500 ml-1">Prospects</span>
                        </div>
                        <div>
                          <span className="font-semibold">
                            {segment.customer_count || 0}
                          </span>
                          <span className="text-gray-500 ml-1">Customers</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/segments/${segment.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => handleEdit(segment)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(segment.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Add/Edit Dialog */}
          {open && (
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex items-center justify-center min-h-screen px-4">
                <div
                  className="fixed inset-0 bg-gray-500/75"
                  onClick={handleClose}
                ></div>

                <div className="relative bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                  <h2 className="text-xl font-semibold mb-4">
                    {editingSegment ? 'Edit Segment' : 'Add New Segment'}
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Segment Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              description: e.target.value,
                            })
                          }
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Focus Mode
                        </label>
                        <select
                          value={formData.focus_mode}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              focus_mode: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {getSelectableFocusModes().map((mode) => (
                            <option key={mode.value} value={mode.value}>
                              {mode.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority
                        </label>
                        <select
                          value={formData.priority}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              priority: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Strategic Owner
                        </label>
                        <select
                          value={formData.strategic_owner_id}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              strategic_owner_id: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">No owner</option>
                          {members.map((member) => (
                            <option key={member.user_id} value={member.user_id}>
                              {member.user_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Group
                        </label>
                        <select
                          value={formData.group_id || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              group_id: e.target.value || null,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">No group</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total Addressable Market
                        </label>
                        <input
                          type="number"
                          value={formData.total_addressable_market}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              total_addressable_market: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Market Maturity
                        </label>
                        <select
                          value={formData.market_maturity}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              market_maturity: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="emerging">Emerging</option>
                          <option value="growth">Growth</option>
                          <option value="mature">Mature</option>
                          <option value="declining">Declining</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        {editingSegment ? 'Save Changes' : 'Add Segment'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {filteredSegments.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm
                  ? 'No segments found matching your search.'
                  : 'No segments yet. Add your first segment to get started.'}
              </p>
            </div>
          )}
        </>
      ) : (
        <GroupsManagement
          organizationId={organization.id}
          groupType="segment"
          canEdit={canEdit}
          canDelete={canDelete}
          onGroupsChange={fetchGroups}
        />
      )}

      <PlanLimitDialog
        open={planLimitDialog.open}
        onClose={() => setPlanLimitDialog({ ...planLimitDialog, open: false })}
        limitType={planLimitDialog.limitType}
        currentPlan={organizationDetails?.plan_type || 'free'}
        currentCount={planLimitDialog.currentCount}
        limit={planLimitDialog.limit}
      />
    </div>
  );
}
